import { Collapse, Grid, IconButton, Typography } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import copy from 'copy-to-clipboard';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React from 'react';
import { OnCopyProps } from 'react-json-view';

import { IStreamResponseData } from '~/src/dash/api/live_logs/models';
import { CopyButton } from '~/src/dash/components/button';
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';

interface IRow {
  timestamp: string;
  networkID: number;
  requestURL: string;
  responseBody: string;
  ua: string;
  xff: string;
  idx: number;
}

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  data: IStreamResponseData[];
}

interface IState extends IBaseState {
  tableData: IRow[];
  activeIndex: number;
}

function dataToRow(data: IStreamResponseData[], publisherTimezone: string): IRow[] {
  if (!Array.isArray(data) || data === undefined) {
    return [];
  }

  let i = -10;
  const mappedData = (data || [])
    .map((d: IStreamResponseData): IRow => {
      i = i + 10;

      return {
        timestamp:
          moment(d.timestamp).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
          '\n' +
          publisherTimezone,
        networkID: d.networkID,
        requestURL: d.requestURL,
        responseBody: d.responseBody,
        ua: d.ua,
        xff: d.xff,
        idx: i,
      };
    })
    .sort((a: IRow, b: IRow): number => {
      // Sort data by timestamp desc
      return (
        moment(b.timestamp, 'DD-MM-YYYY HH:mm:ss').unix() -
        moment(a.timestamp, 'DD-MM-YYYY HH:mm:ss').unix()
      );
    });

  return mappedData;
}

export const LogStreamResponseTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    tableColumns = [
      {
        title: 'Timestamp',
        field: 'timestamp',
        searchable: false,
        width: '40px',
        cellStyle: { whiteSpace: 'pre-wrap' },
      },
      {
        title: 'Request URL',
        field: 'requestURL',
        searchable: false,
        cellStyle: { maxWidth: 1200 },
      },
    ] as Array<Column<IRow>>;

    state = {
      tableData: dataToRow(this.props.data, this.props.session.publisher.Timezone),
      activeIndex: -1,
    };

    handleCopy = (copyObj: OnCopyProps) => {
      copy(JSON.stringify(copyObj.src, null, '\t'));
    };

    handleClick = (index: number) => {
      const { activeIndex } = this.state;
      const newIndex = activeIndex === index ? -1 : index;

      this.setState({ activeIndex: newIndex });
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.data !== this.props.data) {
        this.setState({
          tableData: dataToRow(this.props.data, this.props.session.publisher.Timezone),
        });
      }
    }

    render() {
      return (
        <div>
          <MaterialTable
            detailPanel={(rowData: IRow) => {
              return (
                <Grid container direction='column'>
                  {/* Response Body */}
                  <Grid item style={{ maxWidth: '800px' }}>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx)}>
                          {this.state.activeIndex === rowData.idx ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>Response</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.responseBody} text='Copy' />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx}>
                      <Typography variant='body2' style={{ whiteSpace: 'pre-line' }}>
                        {rowData.responseBody.split('#').join('\n#')}
                      </Typography>
                    </Collapse>
                  </Grid>
                  {/* UA*/}
                  <Grid item style={{ maxWidth: '800px' }}>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <p>User Agent</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.ua} text='Copy' />
                      </Grid>
                    </Grid>
                    <Typography
                      variant='body2'
                      style={{ whiteSpace: 'pre-line', paddingLeft: '20px' }}
                    >
                      {rowData.ua}
                    </Typography>
                  </Grid>
                  {/* XFF*/}
                  <Grid item style={{ maxWidth: '800px' }}>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <p>XFF</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.xff} text='Copy' />
                      </Grid>
                    </Grid>
                    <Typography
                      variant='body2'
                      style={{ whiteSpace: 'pre-line', paddingLeft: '20px' }}
                    >
                      {rowData.xff}
                    </Typography>
                  </Grid>
                </Grid>
              );
            }}
            style={{
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            title=''
            options={{
              search: false,
              pageSize: 15,
              pageSizeOptions: [5, 15, 30],
              toolbar: false,
            }}
            columns={this.tableColumns}
            data={this.state.tableData}
          />
        </div>
      );
    }
  },
);
